import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  Grid,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { OtaFooter, OtaSearchBar, type OtaSearchValue } from '@ota/design-system';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPropertySuggestions, searchProperties } from '../api';
import { ListingSidebar, type FilterState } from '../components/ListingSidebar';
import { ListingSortTabs, type SortKey } from '../components/ListingSortTabs';
import { PropertyListingCard } from '../components/PropertyListingCard';
import { PublicHeader } from '../components/PublicHeader';

const PAGE_SIZE = 10;

export function ListingPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // ----- URL-driven state -----
  const city = search.get('city') ?? '';
  const area = search.get('area') ?? '';
  // q/propertyId come from the hero search bar's combined "city, area or
  // property" box (see HomePage/handleHeroSearch below); city/area are the
  // older structured params, still reachable via direct links (e.g. the
  // homepage's "Popular cities" chips) and still supported by the backend.
  const q = search.get('q') ?? '';
  const propertyId = search.get('propertyId') ?? '';
  const checkIn = search.get('checkIn') ?? '';
  const checkOut = search.get('checkOut') ?? '';
  const guests = search.get('guests') ?? '';
  const sort = (search.get('sort') as SortKey) || 'popularity';

  // Filter state (also URL-driven for shareable links)
  const filters: FilterState = {
    starRatings: (search.get('starRatings') ?? '').split(',').filter(Boolean).map(Number),
    propertyTypeCodes: (search.get('propertyTypeCodes') ?? '').split(',').filter(Boolean),
    amenityCodes: (search.get('amenityCodes') ?? '').split(',').filter(Boolean),
    houseRuleCodes: (search.get('houseRuleCodes') ?? '').split(',').filter(Boolean),
    minPrice: search.get('minPrice') ?? '',
    maxPrice: search.get('maxPrice') ?? '',
    minRating: search.get('minRating') ? Number(search.get('minRating')) : null,
  };

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '' || v === null) next.delete(k);
      else next.set(k, v);
    }
    setSearch(next, { replace: true });
    setPage(0);
  };

  const updateFilters = (patch: Partial<FilterState>) => {
    updateParams({
      ...(patch.starRatings !== undefined && {
        starRatings: patch.starRatings.length === 0 ? undefined : patch.starRatings.join(','),
      }),
      ...(patch.propertyTypeCodes !== undefined && {
        propertyTypeCodes:
          patch.propertyTypeCodes.length === 0 ? undefined : patch.propertyTypeCodes.join(','),
      }),
      ...(patch.amenityCodes !== undefined && {
        amenityCodes: patch.amenityCodes.length === 0 ? undefined : patch.amenityCodes.join(','),
      }),
      ...(patch.houseRuleCodes !== undefined && {
        houseRuleCodes: patch.houseRuleCodes.length === 0 ? undefined : patch.houseRuleCodes.join(','),
      }),
      ...(patch.minPrice !== undefined && { minPrice: patch.minPrice || undefined }),
      ...(patch.maxPrice !== undefined && { maxPrice: patch.maxPrice || undefined }),
      ...(patch.minRating !== undefined && {
        minRating: patch.minRating === null ? undefined : String(patch.minRating),
      }),
    });
  };

  const clearAllFilters = () => {
    updateParams({
      starRatings: undefined,
      propertyTypeCodes: undefined,
      amenityCodes: undefined,
      houseRuleCodes: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      minRating: undefined,
    });
  };

  // ----- search query -----
  // City input is now a free-text field hitting the backend's LIKE search,
  // so the master city list is no longer fetched here.

  const { data, isLoading, isError } = useQuery({
    // checkIn/checkOut included in the queryKey so the cache invalidates when
    // dates change — that's also what drives the Sold Out badge re-render.
    queryKey: ['property-search', { city, area, q, propertyId, checkIn, checkOut, sort, page, filters }],
    queryFn: () =>
      searchProperties({
        city: city || undefined,
        area: area || undefined,
        q: q || undefined,
        propertyId: propertyId ? Number(propertyId) : undefined,
        sort,
        page,
        size: PAGE_SIZE,
        starRatings: filters.starRatings.length > 0 ? filters.starRatings : undefined,
        propertyTypeCodes:
          filters.propertyTypeCodes.length > 0 ? filters.propertyTypeCodes : undefined,
        amenityCodes: filters.amenityCodes.length > 0 ? filters.amenityCodes : undefined,
        houseRuleCodes: filters.houseRuleCodes.length > 0 ? filters.houseRuleCodes : undefined,
        minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
        maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
        minRating: filters.minRating ?? undefined,
        // Forward dates only when BOTH are present — the backend skips
        // availability work when either is missing, so this avoids the
        // per-property ~6-query cost on undated searches.
        checkIn:  checkIn && checkOut ? checkIn  : undefined,
        checkOut: checkIn && checkOut ? checkOut : undefined,
      }),
  });

  const goToDetail = (id: number) => {
    const params = new URLSearchParams();
    if (checkIn) params.set('checkIn', checkIn);
    if (checkOut) params.set('checkOut', checkOut);
    if (guests) params.set('guests', guests);
    const qs = params.toString();
    navigate(`/hotels/${id}${qs ? `?${qs}` : ''}`);
  };

  const handleHeroSearch = (value: OtaSearchValue) => {
    // A fresh bar search fully replaces the location filter — clears the
    // older structured city/area params (e.g. from a "Popular cities" chip
    // click) rather than combining with them, so results match what's
    // actually shown in the box.
    updateParams({
      city: undefined,
      area: undefined,
      q: value.propertyId ? undefined : value.query || undefined,
      propertyId: value.propertyId ? String(value.propertyId) : undefined,
      checkIn: value.checkIn || undefined,
      checkOut: value.checkOut || undefined,
      guests: value.guests ? String(value.guests) : undefined,
    });
  };

  const activeChips = useMemo(() => {
    const chips: Array<{ label: string; clear: () => void }> = [];
    if (filters.minPrice || filters.maxPrice) {
      chips.push({
        label: `₹${filters.minPrice || 0} – ₹${filters.maxPrice || '∞'}`,
        clear: () => updateFilters({ minPrice: '', maxPrice: '' }),
      });
    }
    for (const star of filters.starRatings) {
      chips.push({
        label: `${star} star`,
        clear: () => updateFilters({ starRatings: filters.starRatings.filter((s) => s !== star) }),
      });
    }
    if (filters.minRating) {
      chips.push({
        label: `Rating ${filters.minRating}+`,
        clear: () => updateFilters({ minRating: null }),
      });
    }
    for (const t of filters.propertyTypeCodes) {
      chips.push({
        label: t,
        clear: () =>
          updateFilters({ propertyTypeCodes: filters.propertyTypeCodes.filter((c) => c !== t) }),
      });
    }
    for (const a of filters.amenityCodes) {
      chips.push({
        label: a,
        clear: () =>
          updateFilters({ amenityCodes: filters.amenityCodes.filter((c) => c !== a) }),
      });
    }
    for (const h of filters.houseRuleCodes) {
      chips.push({
        label: h,
        clear: () =>
          updateFilters({ houseRuleCodes: filters.houseRuleCodes.filter((c) => c !== h) }),
      });
    }
    return chips;
  }, [filters]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.subtle',
      }}
    >
      <PublicHeader />

      {/* Compact search bar at the top */}
      <Box sx={{ bgcolor: 'primary.main', py: 2 }}>
        <Container maxWidth="xl">
          <OtaSearchBar
            variant="compact"
            initial={{
              // Fall back to city so the box still shows something meaningful
              // when arriving via a direct city link rather than the bar itself.
              query: q || city || '',
              propertyId: propertyId ? Number(propertyId) : undefined,
              checkIn, checkOut,
              guests: Number(guests) || 2,
            }}
            onSearch={handleHeroSearch}
            onSuggest={fetchPropertySuggestions}
          />
        </Container>
      </Box>

      {/* Mobile filter drawer */}
      <Drawer
        anchor="left"
        open={isMobile && filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, p: 2 } }}
      >
        <ListingSidebar
          filters={filters}
          onChange={(patch) => {
            updateFilters(patch);
          }}
          onClearAll={() => {
            clearAllFilters();
            setFilterDrawerOpen(false);
          }}
          resultsCount={data?.page.totalElements}
        />
      </Drawer>

      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, flex: 1 }}>
        <Grid container spacing={3}>
          {/* Sidebar — desktop only; mobile uses the Drawer above */}
          <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Card
              sx={{
                p: 2,
                position: 'sticky',
                top: 88,
                maxHeight: 'calc(100vh - 96px)',
                overflowY: 'auto',
              }}
            >
              <ListingSidebar
                filters={filters}
                onChange={updateFilters}
                onClearAll={clearAllFilters}
                resultsCount={data?.page.totalElements}
              />
            </Card>
          </Grid>

          {/* Main column */}
          <Grid item xs={12} md={9}>
            <Stack spacing={2}>
              {/* Heading + filter button (mobile) + sort tabs */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '2rem' } }}
                  >
                    {data?.page.totalElements ?? 0} Properties
                    {city && <> in {city}</>}
                    {!city && q && <> for &ldquo;{q}&rdquo;</>}
                  </Typography>
                  {area && (
                    <Typography variant="body2" color="text.secondary">
                      Filtered to {area}
                    </Typography>
                  )}
                </Stack>
                {/* Mobile-only filter button */}
                <Badge
                  badgeContent={activeChips.length || undefined}
                  color="secondary"
                  sx={{ display: { xs: 'flex', md: 'none' } }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterDrawerOpen(true)}
                  >
                    Filters
                  </Button>
                </Badge>
              </Stack>

              <ListingSortTabs value={sort} onChange={(k) => updateParams({ sort: k })} />

              {/* Active filter chips */}
              {activeChips.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {activeChips.map((c) => (
                    <Chip key={c.label} label={c.label} onDelete={c.clear} size="small" />
                  ))}
                </Stack>
              )}

              {/* Results */}
              {isLoading && <CircularProgress />}
              {isError && (
                <Alert severity="warning">
                  Unable to load properties right now. Please try again in a moment.
                </Alert>
              )}
              {!isLoading && !isError && (data?.content.length ?? 0) === 0 && (
                <Alert severity="info">
                  No properties found for your search. Try a different city, or clear some filters.
                </Alert>
              )}

              <Stack spacing={2}>
                {(data?.content ?? []).map((p) => (
                  <PropertyListingCard key={p.id} property={p} onClick={() => goToDetail(p.id)} />
                ))}
              </Stack>

              {data && data.page.totalPages > 1 && (
                <Stack alignItems="center" sx={{ pt: 2 }}>
                  <Pagination
                    count={data.page.totalPages}
                    page={page + 1}
                    onChange={(_, p) => setPage(p - 1)}
                    color="primary"
                  />
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <OtaFooter />
    </Box>
  );
}
